import { type ProConId, type SuretyAccountId } from "@inrev/common";
import { useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import {
	dataNeededQueryKey,
	proConRelationshipsQueryKey,
	proConsQueryKey,
	resolvedDataNeededQueryKey,
	retiredProConsQueryKey,
} from "../decisioning.keys";
import { type DataNeeded, type ProCon } from "../types";
import { invalidateListWhenIdle, useOptimisticListMutation } from "../useOptimisticListMutation";

const withoutLinkTo = (items: DataNeeded[], id: ProConId): DataNeeded[] =>
	items.map((item) => (item.proConId === id ? { ...item, proConId: null } : item));

/** Soft-deletes a pro/con (active or retired); data needed that pointed at it loses the link. */
export const useDeleteProCon = (accountId: SuretyAccountId) => {
	const { _delete } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useOptimisticListMutation<ProCon, ProConId>({
		queryKey: proConsQueryKey(accountId),
		request: async (id) => {
			await _delete(`/v2/admin/surety/accounts/${accountId}/pro-cons/${id}`, undefined, "none");
		},
		apply: (items, id) => items.filter((item) => item.id !== id),
		affectedIds: (id) => [id],
		failureMessage: () => "Couldn't delete the factor.",
		reconcile: (items, _response, id) => {
			queryClient.setQueryData<ProCon[]>(
				retiredProConsQueryKey(accountId),
				(retired) => retired && retired.filter((item) => item.id !== id),
			);
			for (const key of [dataNeededQueryKey(accountId), resolvedDataNeededQueryKey(accountId)]) {
				queryClient.setQueryData<DataNeeded[]>(
					key,
					(cached) => cached && withoutLinkTo(cached, id),
				);
			}
			void invalidateListWhenIdle(queryClient, proConRelationshipsQueryKey(accountId));
			return items;
		},
	});

	return {
		deleteProCon: mutation.mutate,
	};
};
